/**
 * 招待情報取得用のhooks
 * 認証不要の公開エンドポイントを使用
 */

import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

/**
 * 公開招待情報の型
 */
export type PublicInvitation = {
  tenantName: string;
  email: string;
  role: string;
  expiresAt: string;
  status: string;
};

/**
 * 招待承認の結果に含まれるテナント情報
 */
export type AcceptedTenant = {
  id: string;
  name: string;
};

/**
 * 招待承認hookの結果
 */
export type UseAcceptInvitationResult = {
  acceptInvitation: (token: string) => Promise<AcceptedTenant>;
  loading: boolean;
  error: RestApiError | null;
};

/**
 * 招待情報取得hookの結果
 */
export type UseInvitationResult = {
  data: PublicInvitation | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
};

/**
 * トークンから招待情報を取得するhook
 * @param {string} token - 招待トークン
 * @return {UseInvitationResult} 招待情報、ローディング状態、エラー
 */
export function useInvitation(token: string): UseInvitationResult {
  const [data, setData] = useState<PublicInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchInvitation = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await restClient.publicGet<PublicInvitation>(
        `/public/invitations/${token}`,
      );
      setData(response);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInvitation();
  }, [fetchInvitation]);

  return { data, loading, error, refetch: fetchInvitation };
}

/**
 * 招待を承認してテナントに参加するhook (要認証)
 *
 * 招待の自動参加はサインアップ経路 (PostConfirmation) にしか無いため、
 * 招待より前からアカウントがあるユーザーはこの明示的な承認が必要になる。
 * @return {UseAcceptInvitationResult} 承認関数、ローディング状態、エラー
 */
export function useAcceptInvitation(): UseAcceptInvitationResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const acceptInvitation = useCallback(
    async (token: string): Promise<AcceptedTenant> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.post<{ tenant: AcceptedTenant }>(
          `/invitations/${token}/accept`,
        );
        return response.tenant;
      } catch (e) {
        const restError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(restError);
        throw restError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { acceptInvitation, loading, error };
}
